import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectsAPI, locationsAPI, usersAPI, clientsAPI } from '../api/client';
import { EVENT_TYPES, RISK_LEVEL, TIMELINE_KEYS, AUDIENCE_TYPES, AGE_RANGES } from '../lib/eventConstants';
import { AlertTriangle, ChevronLeft, ChevronRight, X, Check, MapPin } from 'lucide-react';
import { isAvailableLocation, locationClientLabel, loadLocationOptions } from '../lib/locationSelection';
import { toast } from 'sonner';

function StepBar({ step }) {
  const steps = ['Información', 'Fechas', 'Asignación'];
  return (
    <div className="flex items-center gap-0 mb-6">
      {steps.map((s, i) => (
        <React.Fragment key={i}>
          <div className="flex flex-col items-center">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
              i < step ? 'bg-soem-primary border-soem-primary text-white' :
              i === step ? 'border-soem-primary text-soem-primary bg-white' :
              'border-slate-300 text-slate-400 bg-white'
            }`}>
              {i < step ? <Check size={12} /> : i + 1}
            </div>
            <span className={`text-xs mt-1 ${i === step ? 'text-soem-primary font-semibold' : 'text-slate-400'}`}>{s}</span>
          </div>
          {i < steps.length - 1 && (
            <div className={`flex-1 h-0.5 mb-4 mx-1 transition-colors ${i < step ? 'bg-soem-primary' : 'bg-slate-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function MiniTimeline({ dates }) {
  const now = new Date();
  const items = [
    { key: 'setup_start', label: 'Montaje' },
    { key: 'doors_open', label: 'Apertura' },
    { key: 'event_start', label: 'Inicio' },
    { key: 'event_end', label: 'Fin' },
    { key: 'teardown_date', label: 'Desmontaje' },
  ];
  const filled = items.filter(i => dates[i.key]);
  if (filled.length < 2) return null;
  return (
    <div className="mt-3 p-3 bg-slate-50 rounded-sm border border-slate-200">
      <p className="soem-label mb-2">Vista previa del cronograma</p>
      <div className="flex items-start justify-between relative">
        <div className="absolute top-3 left-0 right-0 h-0.5 bg-slate-300" />
        {items.map((item, i) => {
          const d = dates[item.key];
          const isPast = d && new Date(d) < now;
          return (
            <div key={item.key} className="flex flex-col items-center relative z-10">
              <div className={`w-6 h-6 rounded-full border-2 text-xs flex items-center justify-center font-bold ${
                d ? (isPast ? 'bg-slate-400 border-slate-400 text-white' : 'bg-soem-primary border-soem-primary text-white')
                  : 'bg-white border-slate-300 text-slate-300'
              }`}>{i + 1}</div>
              <p className="text-xs text-slate-500 mt-1 text-center leading-tight" style={{ fontSize: '10px' }}>{item.label}</p>
              {d && <p className="text-slate-400 text-center" style={{ fontSize: '9px' }}>{new Date(d).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function CreateProjectEventModal({ project, onCreated, onClose }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [locSearch, setLocSearch] = useState('');
  const [locResults, setLocResults] = useState([]);
  const [locationNotice, setLocationNotice] = useState('');
  const [selectedLoc, setSelectedLoc] = useState(null);
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [clients, setClients] = useState([]);
  const [leaders, setLeaders] = useState([]);

  const [form, setForm] = useState({
    name: '', event_type: 'concierto', risk_level: 'medio',
    location_id: '',
    dates: { setup_start: '', doors_open: '', event_start: '', event_end: '', teardown_date: '' },
    expected_capacity: '', crowd_management: 'parados',
    assigned_levantador_id: '', notes: '',
    // New fields
    audience_type: [],
    audience_age_range: [],
    client_id: project?.client_id || '',
    leader_id: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setDate = (k, v) => setForm(f => ({ ...f, dates: { ...f.dates, [k]: v } }));
  const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary";

  // #AutoCalcDates — Paridad con EventForm.jsx. Al fijar el inicio de
  // montaje pre-cargamos:
  //   apertura de puertas = montaje +2 días
  //   inicio del evento   = montaje +3 días
  //   fin del evento      = montaje +4 días
  //   desmontaje          = montaje +6 días
  // Sólo se llenan slots que el usuario NO haya tocado (vacíos), para no
  // sobrescribir ajustes manuales posteriores.
  const setSetupStartAndDerive = (val) => {
    setForm((f) => {
      const newDates = { ...f.dates, setup_start: val };
      if (val) {
        const base = new Date(val);
        if (!isNaN(base.getTime())) {
          const fmt = (d) => {
            const pad = (n) => String(n).padStart(2, '0');
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
          };
          const addDays = (n) => {
            const d = new Date(base);
            d.setDate(d.getDate() + n);
            return fmt(d);
          };
          const offsets = { doors_open: 2, event_start: 3, event_end: 4, teardown_date: 6 };
          for (const [key, days] of Object.entries(offsets)) {
            if (!newDates[key]) newDates[key] = addDays(days);
          }
        }
      }
      return { ...f, dates: newDates };
    });
  };

  const [allUsers, setAllUsers] = useState([]);
  
  useEffect(() => {
    // Load users, locations and clients
    Promise.all([
      usersAPI.list({ limit: 500 }),
      loadLocationOptions(locationsAPI.list).then(items => ({ data: { items } })),
      clientsAPI.list(),
    ]).then(([usersRes, locsRes, clientsRes]) => {
      const items = usersRes.data.items || [];
      // #IncludeAdminsAsLevantadores — incluye super_admin y lider_proyecto
      // junto con levantadores; los admins también pueden fungir como
      // levantadores asignados a un evento.
      // #LeaderDropdownFix — Backend usa `status: 'activo'` no `is_active`;
      // si el doc no tiene `is_active` o es undefined, el strict truthy
      // dejaba la lista vacía. Filtro defensivo: aceptamos cualquier
      // valor salvo explicit false/'inactivo'/'archivado'.
      const isActiveUser = (u) =>
        u.is_active !== false &&
        u.status !== 'inactivo' &&
        u.status !== 'archivado';
      const levantadores = items
        .filter(u => ['super_admin', 'lider_proyecto', 'levantador'].includes(u.role) && isActiveUser(u))
        .sort((a, b) => (`${a.last_name || ''} ${a.first_name || ''}`).localeCompare(`${b.last_name || ''} ${b.first_name || ''}`, 'es'));
      setUsers(levantadores);

      // Sort leaders alphabetically (sin filter is_active strict que dejaba vacío)
      const leadersList = items
        .filter(u => ['lider_proyecto', 'super_admin'].includes(u.role) && isActiveUser(u))
        .sort((a, b) => (`${a.last_name || ''} ${a.first_name || ''}`).localeCompare(`${b.last_name || ''} ${b.first_name || ''}`, 'es'));
      setAllUsers(leadersList);
      setLeaders(leadersList);
      
      // Sort locations alphabetically
      const locs = (locsRes.data.items || [])
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es'));
      setLocations(locs);
      
      // Sort clients alphabetically
      const clientsList = (clientsRes.data.items || [])
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es'));
      setClients(clientsList);
    }).catch(() => {});
  }, []);

  const searchLoc = async (q) => {
    if (!q || q.length < 2) { 
      setLocResults([]); 
      return; 
    }
    // Filter from pre-loaded locations
    const filtered = locations.filter(loc => 
      loc.name.toLowerCase().includes(q.toLowerCase()) ||
      (loc.address?.city || '').toLowerCase().includes(q.toLowerCase())
    );
    setLocResults(filtered.slice(0, 10));
  };

  const eventClientId = form.client_id || project?.client_id || '';
  const availableLocations = locations.filter(loc => isAvailableLocation(loc, eventClientId));
  const changeClient = (clientId) => {
    if (clientId === form.client_id) return;
    const keepLocation = selectedLoc && isAvailableLocation(selectedLoc, clientId || project?.client_id);
    setForm(f => ({ ...f, client_id: clientId, location_id: keepLocation ? f.location_id : '' }));
    setLocationNotice(form.location_id && !keepLocation ? 'La locación elegida no es de este cliente; elige otra.' : '');
    if (!keepLocation) { setSelectedLoc(null); setStep(0); }
  };

  const dateErr = () => {
    const { event_start, event_end } = form.dates;
    if (event_start && event_end && new Date(event_start) >= new Date(event_end))
      return 'El fin debe ser posterior al inicio.';
    return null;
  };

  const canNext = () => {
    if (step === 0) return form.name.trim() && form.location_id;
    if (step === 1) return form.dates.event_start && form.dates.event_end && !dateErr();
    return true;
  };

  const handleCreate = async () => {
    if (!form.location_id) { toast.error('Selecciona una locación'); setStep(0); return; }
    setSaving(true);
    try {
      // Antes mandábamos risk_level y expected_capacity, pero el backend
      // lee body.get("projected_risk_level") y body.get("expected_attendance")
      // (#bh8Tivum). Los campos se ignoraban silenciosamente, el evento
      // se guardaba con risk_level="medio" default y expected_attendance=None.
      // En el UI parecía que no se generaba el registro porque los datos
      // tampoco aparecían correctamente.
      const payload = {
        name: form.name,
        event_type: form.event_type,
        projected_risk_level: form.risk_level,
        location_id: form.location_id,
        client_id: form.client_id || project.client_id,
        dates: Object.fromEntries(Object.entries(form.dates).filter(([, v]) => v)),
        expected_attendance: form.expected_capacity ? parseInt(form.expected_capacity) : null,
        crowd_management: form.crowd_management,
        assigned_levantador_id: form.assigned_levantador_id || null,
        notes: form.notes || null,
        // New fields
        assigned_leader_id: form.leader_id || null,
        audience_profile: {
          age_ranges: form.audience_age_range || [],
          audience_types: form.audience_type || [],
        },
      };
      const r = await projectsAPI.createEvent(project.id, payload);
      toast.success(`Evento "${r.data.name}" creado`);
      onCreated(r.data);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Error al guardar el evento');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-soem-navy sticky top-0">
          <div>
            <h3 className="font-bold text-white text-sm">Nuevo Evento</h3>
            <p className="text-slate-400 text-xs mt-0.5">{project.name}</p>
          </div>
          <button aria-label="Cerrar" onClick={onClose}><X size={18} className="text-slate-400 hover:text-white" /></button>
        </div>

        <div className="p-5">
          <StepBar step={step} />

          {/* Step 0: Información básica */}
          {step === 0 && (
            <div className="space-y-4">
              <div>
                <label htmlFor="f-nombre-del-evento" className="soem-label block mb-1.5">Nombre del evento <span className="text-red-500">*</span></label>
                <input id="f-nombre-del-evento" className={inputCls} value={form.name} onChange={e => set('name', e.target.value)} autoFocus data-testid="evt-name" />
              </div>
              <div>
                <label htmlFor="f-tipo-de-evento" className="soem-label block mb-2">Tipo de evento</label>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(EVENT_TYPES).map(([k, v]) => (
                    <button key={k} type="button" onClick={() => set('event_type', k)}
                      className={`flex flex-col items-center gap-1 p-2.5 rounded-sm border-2 text-xs font-semibold transition-all ${form.event_type === k ? 'border-soem-primary bg-blue-50 text-soem-primary' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                      <span className="text-xl">{v.emoji}</span>
                      <span className="leading-tight text-center" style={{ fontSize: '10px' }}>{v.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label htmlFor="f-nivel-de-riesgo" className="soem-label block mb-2">Nivel de riesgo</label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(RISK_LEVEL).map(([k, v]) => (
                    <button key={k} type="button" onClick={() => set('risk_level', k)}
                      className={`py-2 px-3 rounded-sm border-2 font-bold text-sm transition-all ${form.risk_level === k ? `text-white border-transparent` : 'border-slate-200 text-slate-600'}`}
                      style={form.risk_level === k ? { backgroundColor: v.bar } : {}}>
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="relative">
                {!eventClientId && <p className="text-sm text-slate-600">Selecciona el cliente para ver sus locaciones. Por ahora solo se muestran venues sin cliente asignado.</p>}
                {locationNotice && <p role="status" className="text-sm text-amber-700">{locationNotice}</p>}
                <label htmlFor="f-locación" className="soem-label block mb-1.5">Locación <span className="text-red-500">*</span></label>
                {/* Dropdown alfabético en lugar del autocomplete textual (#40). */}
                <select
                  id="f-locación"
                  className={inputCls}
                  value={form.location_id}
                  onChange={(e) => {
                    const lid = e.target.value;
                    set('location_id', lid);
                    setLocationNotice('');
                    const loc = locations.find(l => l.id === lid) || null;
                    setSelectedLoc(loc);
                    setLocSearch(loc?.name || '');
                  }}
                >
                  <option value="">— Seleccionar locación activa —</option>
                  {availableLocations.map(loc => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name} · {locationClientLabel(loc, clients)}{loc.address?.city ? ` · ${loc.address.city}` : ''}
                    </option>
                  ))}
                </select>
                {selectedLoc && (
                  <div className="mt-2 bg-blue-50 border border-blue-200 rounded-sm px-3 py-2 text-xs text-blue-700 flex items-center gap-1.5">
                    <MapPin size={11} /> {selectedLoc.name}{selectedLoc.address?.city ? ` · ${selectedLoc.address.city}` : ''}
                    {selectedLoc.max_capacity ? ` · Aforo: ${selectedLoc.max_capacity.toLocaleString()}` : ''}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 1: Fechas */}
          {step === 1 && (
            <div className="space-y-3">
              {/* #AutoCalcDates — hint para que el usuario sepa que al fijar
                  el montaje se autollena el resto, pero puede editarlas. */}
              <p className="text-xs text-slate-500">
                Al fijar el <strong>inicio de montaje</strong> se pre-llenan automáticamente:
                apertura +2 días · inicio +3 días · fin +4 días · desmontaje +6 días.
                Puedes ajustar cada fecha manualmente.
              </p>
              {dateErr() && (
                <div className="bg-orange-50 border border-orange-200 rounded-sm p-2 flex items-center gap-2 text-orange-700 text-xs">
                  <AlertTriangle size={13} /> {dateErr()}
                </div>
              )}
              {[
                { k: 'setup_start', l: 'Inicio de montaje' },
                { k: 'doors_open', l: 'Apertura de puertas' },
                { k: 'event_start', l: 'Inicio del evento', req: true },
                { k: 'event_end', l: 'Fin del evento', req: true },
                { k: 'teardown_date', l: 'Desmontaje' },
              ].map(({ k, l, req }) => (
                <div key={k}>
                  <label htmlFor={`f-date-${k}`} className="soem-label block mb-1">{l}{req && <span className="text-red-500"> *</span>}</label>
                  <input id={`f-date-${k}`} type="datetime-local" className={inputCls} value={form.dates[k]}
                    onChange={e => {
                      // Sólo setup_start dispara la cascada de auto-calc;
                      // el resto usa setDate normal para que el usuario
                      // pueda ajustarlos sin efectos colaterales.
                      if (k === 'setup_start') {
                        setSetupStartAndDerive(e.target.value);
                      } else {
                        setDate(k, e.target.value);
                      }
                    }}
                    required={req} />
                </div>
              ))}
              <MiniTimeline dates={form.dates} />
            </div>
          )}

          {/* Step 2: Asignación */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="f-aforo-esperado" className="soem-label block mb-1.5">Aforo esperado</label>
                  <input id="f-aforo-esperado" type="number" min="0" className={inputCls} value={form.expected_capacity}
                    onChange={e => set('expected_capacity', e.target.value)} placeholder="0" />
                </div>
                <div>
                  <label htmlFor="f-manejo-de-multitudes" className="soem-label block mb-1.5">Manejo de multitudes</label>
                  <select id="f-manejo-de-multitudes" className={inputCls} value={form.crowd_management} onChange={e => set('crowd_management', e.target.value)}>
                    <option value="parados">Parados</option>
                    <option value="sentados">Sentados</option>
                    <option value="hibrido">Híbrido</option>
                  </select>
                </div>
              </div>
              {/* New Fields - TAREA 7 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="f-promotor-cliente" className="soem-label block mb-1.5">Promotor / Cliente</label>
                  <select id="f-promotor-cliente" className={inputCls} value={form.client_id} onChange={e => changeClient(e.target.value)}>
                    <option value="">— Seleccionar cliente —</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="f-líder-de-proyecto" className="soem-label block mb-1.5">Líder de Proyecto</label>
                  <select id="f-líder-de-proyecto" className={inputCls} value={form.leader_id} onChange={e => set('leader_id', e.target.value)}>
                    <option value="">— Sin asignar —</option>
                    {/* #LeaderDropdownFix — fallback chain de nombre: full_name → name → "last first" → email */}
                    {leaders.map(u => {
                      const label = u.full_name || u.name || (`${u.last_name || ''} ${u.first_name || ''}`.trim()) || u.email || u.id;
                      return <option key={u.id} value={u.id}>{label}</option>;
                    })}
                  </select>
                </div>
              </div>
              {/* Audience profile (#41) — campos faltantes en el formulario. */}
              <div>
                <label className="soem-label block mb-1.5">Rangos de edad de la audiencia</label>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                  {AGE_RANGES.map(r => {
                    const checked = (form.audience_age_range || []).includes(r);
                    return (
                      <label key={r} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-sm border cursor-pointer text-xs ${checked ? 'border-soem-primary bg-blue-50 text-soem-primary' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const list = form.audience_age_range || [];
                            set('audience_age_range', checked ? list.filter(x => x !== r) : [...list, r]);
                          }}
                        />
                        {r}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="soem-label block mb-1.5">Tipo de audiencia</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                  {AUDIENCE_TYPES.map(t => {
                    const id = t.id ?? t;
                    const label = t.label ?? t;
                    const checked = (form.audience_type || []).includes(id);
                    return (
                      <label key={id} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-sm border cursor-pointer text-xs ${checked ? 'border-soem-primary bg-blue-50 text-soem-primary' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const list = form.audience_type || [];
                            set('audience_type', checked ? list.filter(x => x !== id) : [...list, id]);
                          }}
                        />
                        {label}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <label htmlFor="f-levantador-asignado" className="soem-label block mb-1.5">Levantador asignado</label>
                <select id="f-levantador-asignado" className={inputCls} value={form.assigned_levantador_id} onChange={e => set('assigned_levantador_id', e.target.value)}>
                  <option value="">— Sin asignar —</option>
                  {users.map(u => {
                    const label = u.full_name || u.name || (`${u.last_name || ''} ${u.first_name || ''}`.trim()) || u.email || u.id;
                    return <option key={u.id} value={u.id}>{label}</option>;
                  })}
                </select>
              </div>
              <div>
                <label htmlFor="f-notas-iniciales" className="soem-label block mb-1.5">Notas iniciales</label>
                <textarea id="f-notas-iniciales" className={`${inputCls} resize-none`} rows={3} value={form.notes}
                  onChange={e => set('notes', e.target.value)} placeholder="Observaciones del evento..." />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 space-y-1 text-xs">
                <p className="font-semibold text-blue-800">Resumen del evento</p>
                <p className="text-blue-600"><strong>{form.name}</strong> · {EVENT_TYPES[form.event_type]?.emoji} {EVENT_TYPES[form.event_type]?.label}</p>
                {selectedLoc && <p className="text-blue-600"><strong>Locación:</strong> {selectedLoc.name}</p>}
                {form.dates.event_start && <p className="text-blue-600"><strong>Fecha:</strong> {new Date(form.dates.event_start).toLocaleString('es-MX')}</p>}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            {step > 0 && (
              <button onClick={() => setStep(s => s - 1)} className="soem-btn-secondary px-4 py-2.5 text-sm flex items-center gap-1.5">
                <ChevronLeft size={14} /> Anterior
              </button>
            )}
            {step < 2 ? (
              <button onClick={() => setStep(s => s + 1)} disabled={!canNext()}
                className="soem-btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-1.5 disabled:opacity-60">
                Siguiente <ChevronRight size={14} />
              </button>
            ) : (
              <button onClick={handleCreate} disabled={saving}
                className="soem-btn-primary flex-1 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="create-event-submit">
                {saving ? <><span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> Creando...</> : '✓ Crear Evento'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
